import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/app-store-hooks';
import { goTo, goToSignIn } from 'router/go-to';
import { userCookieStorage } from 'core/authentification';
import { setUserFromCookieStorageActionCreator } from 'store/auth/actions/auth-action-creators';
import { userIsAnonymousSelector } from 'store/selectors/app-selectors';
import { useRouteParam } from 'hooks/use-query/use-query';
import { useIsFreemiumUser } from 'hooks/use-freemium-user/use-freemium-user';
import { Desktop, Mobile } from 'features/mobile-report/hoc/media-query';
import { MobileApp } from './mobile-app';
import { DesktopAppMemo } from './desktop-app';
import { useInitialStateQuery } from 'store/use-initial-state-query';
import { useInvalidateUser } from 'store/auth/hooks/use-invalidate-user';
import { shouldRenderMobileApp } from 'utils/detect-environment';
import { checkIfUrlSafe } from 'store/auth/utils/utils';
import { getFeatureFlags } from 'core/flow-control';
import { useSessionValidatorService } from './infra';
import { UnRegisterCallback } from '@zdrbm/zdr-native-tools';

export const Dashboard = () => {
    const dispatch = useAppDispatch();
    const userIsAnonymous = useSelector(userIsAnonymousSelector);
    const { enable_session_FE_validation } = getFeatureFlags();
    const isFreemiumUser = useIsFreemiumUser();
    const nextUrl = useRouteParam('next_url');
    const platform = useRouteParam('platform');
    const { isSuccess, error } = useInitialStateQuery();
    const invalidateUser = useInvalidateUser();
    const sessionValidatorService = useSessionValidatorService();

    window.isUserFreemium = isFreemiumUser;

    const userLoggedIn = useMemo(
        () => userCookieStorage.user && userIsAnonymous === false,
        [userIsAnonymous],
    );

    const handleInvalidSession = useCallback(() => {
        invalidateUser(false);
        goToSignIn({
            nextUrl,
        });
        sessionValidatorService.stopSessionValidation();
    }, [invalidateUser, nextUrl, sessionValidatorService]);

    useEffect(() => {
        if (platform === 'mobile') {
            invalidateUser();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let unsubscribe: UnRegisterCallback;

        if (enable_session_FE_validation && userLoggedIn) {
            unsubscribe = sessionValidatorService.isSessionValid.reo(({ value }) => {
                if (!value) {
                    handleInvalidSession();
                }
            });

            sessionValidatorService.startSessionValidation();
        }

        return () => {
            sessionValidatorService.stopSessionValidation();
            unsubscribe && unsubscribe();
        };
    }, [handleInvalidSession, enable_session_FE_validation, sessionValidatorService, userLoggedIn]);

    useEffect(() => {
        if (!shouldRenderMobileApp() && isSuccess && nextUrl && checkIfUrlSafe(nextUrl)) {
            goTo(nextUrl.replace(`${window.location.origin}`, ''));
        } else if (userIsAnonymous === undefined && userCookieStorage.user) {
            dispatch(setUserFromCookieStorageActionCreator(userCookieStorage.user));
        }
    }, [userIsAnonymous, nextUrl, dispatch, isSuccess]);

    useEffect(() => {
        if (error?.status === 401) {
            handleInvalidSession();
        }
    }, [error, handleInvalidSession]);

    return (
        <>
            <Desktop>
                <DesktopAppMemo isReady={isSuccess} />
            </Desktop>
            <Mobile>
                <MobileApp />
            </Mobile>
        </>
    );
};
